import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Stack,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Divider,
  Card,
  CardActionArea,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  InputAdornment,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ContentCopy as DuplicateIcon,
  AttachMoney as ReceiveNowIcon,
  Discount as DiscountIcon,
  Receipt as ReceiptIcon,
  AttachFile as AttachIcon,
  AccountBalance as BankIcon,
  CalendarMonth as CalendarIcon,
  Visibility as ViewIcon,
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Image as ImageIcon,
  Download as DownloadIcon,
  LocalHospital as ConsultaIcon,
  HealthAndSafety as ConvenioIcon,
  EditNote as ManualIcon,
  Person as PatientIcon,
  TrendingUp as TrendingUpIcon,
  Repeat as RepeatIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { getApiUrl } from '../../config/environment';
import { usePageBreakpoints, pageContainerSx } from '../../hooks/useClinicPageBreakpoints';
import { useLocation, useNavigate } from 'react-router-dom';
import PatientAutocomplete from '../../components/PatientAutocomplete';
import ProfessionalAutocomplete, { ProfessionalOption } from '../../components/ProfessionalAutocomplete';
import { useAuth } from '../../contexts/AuthContext';
import { handleCashRegisterRequired } from '../../utils/cashRegisterPrompt';

/* ═══════════════════════════════════════════════════════════════
   TYPES & INTERFACES
   ═══════════════════════════════════════════════════════════════ */

interface AccountReceivable {
  id: string;
  description: string;
  category: string;
  amount: number;
  amount_paid: number;
  /** Alias retornado em alguns payloads da API (compat) */
  paid_amount?: number;
  payment_method: string;
  due_date: string;
  status: string;
  notes?: string;
  patient_id?: string;
  patient_name?: string;
  professional_id?: string;
  professional_name?: string;
  clinic_patients?: { full_name: string };
  clinic_professionals?: { full_name: string };
  clinic_insurances?: { name: string };
  insurance_id?: string;
  invoice_number?: string;
  installment_number?: number;
  installment_total?: number;
  installments?: Installment[];
  payments?: PaymentRecord[];
  attachments?: Attachment[];
}

interface Installment {
  id?: string;
  number: number;
  due_date: string;
  amount: number;
  percentage: number;
  status: string;
}

interface PaymentRecord {
  id?: string;
  date: string;
  amount: number;
  discount: number;
  interest: number;
  penalty: number;
  bank: string;
  notes?: string;
}

interface Attachment {
  id?: string;
  name: string;
  url?: string;
  file_url?: string;
  type: string;
  size?: number;
  uploaded_at: string;
}

type EntryMode = 'consulta' | 'convenio' | 'manual';

interface FixedRevenue {
  id: string;
  description: string;
  category: string;
  amount: number;
  frequency: string;
  due_day: number;
  patient_name?: string;
  is_active: boolean;
  notes?: string;
}

/* ═══════════════════════════════════════════════════════════════
   CONSTANTS
   ═══════════════════════════════════════════════════════════════ */

const ACCENT = '#10b981';
const ACCENT_HOVER = '#059669';

const CATEGORIES = [
  { value: 'consulta_medica', label: 'Consulta Médica' },
  { value: 'procedimento', label: 'Procedimento' },
  { value: 'exame', label: 'Exame' },
  { value: 'cirurgia', label: 'Cirurgia' },
  { value: 'retorno', label: 'Retorno' },
  { value: 'avaliacao', label: 'Avaliação' },
  { value: 'tratamento', label: 'Tratamento Estético' },
  { value: 'convenio', label: 'Convênio / Plano' },
  { value: 'particular', label: 'Particular' },
  { value: 'receita_operacional', label: 'Receita Operacional' },
  { value: 'receita_nao_operacional', label: 'Receita Não Operacional' },
  { value: 'aluguel_sala', label: 'Aluguel de Sala' },
  { value: 'venda_produtos', label: 'Venda de Produtos' },
  { value: 'comissao', label: 'Comissão' },
  { value: 'outros', label: 'Outros' },
];

const PAYMENT_METHODS = [
  { value: 'dinheiro', label: 'Dinheiro' },
  { value: 'pix', label: 'PIX' },
  { value: 'cartao_credito', label: 'Cartão de Crédito' },
  { value: 'cartao_debito', label: 'Cartão de Débito' },
  { value: 'boleto', label: 'Boleto' },
  { value: 'transferencia', label: 'Transferência' },
  { value: 'convenio', label: 'Convênio / Plano' },
  { value: 'cheque', label: 'Cheque' },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'pending', label: 'Pendente' },
  { value: 'paid', label: 'Recebido' },
  { value: 'overdue', label: 'Atrasado' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'partial', label: 'Parcial' },
];

const FREQUENCY_OPTIONS = [
  { value: 'monthly', label: 'Mensal' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'biweekly', label: 'Quinzenal' },
  { value: 'yearly', label: 'Anual' },
];

/* ═══════════════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════════════ */

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const formatDate = (date: string) => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

const getStatusColor = (status: string): 'default' | 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'paid': return 'success';
    case 'pending': return 'warning';
    case 'overdue': return 'error';
    case 'cancelled': return 'default';
    case 'partial': return 'info';
    default: return 'info';
  }
};

const getStatusLabel = (status: string) => {
  switch (status) {
    case 'paid': return 'Recebido';
    case 'pending': return 'Pendente';
    case 'overdue': return 'Atrasado';
    case 'cancelled': return 'Cancelado';
    case 'partial': return 'Parcial';
    default: return status;
  }
};

const getCategoryLabel = (value: string) =>
  CATEGORIES.find((c) => c.value === value)?.label || value;

const getPaymentMethodLabel = (value: string) =>
  PAYMENT_METHODS.find((m) => m.value === value)?.label || value;

const getFrequencyLabel = (value: string) =>
  FREQUENCY_OPTIONS.find((f) => f.value === value)?.label || value;

interface TabPanelProps { children?: React.ReactNode; index: number; value: number; }
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ pt: 2 }}>
    {value === index && children}
  </Box>
);

const emptyForm = () => ({
  description: '',
  category: '',
  amount: '',
  payment_method: '',
  due_date: '',
  status: 'pending',
  notes: '',
  patient_id: '' as string,
  patient_name: '',
  insurance_id: '',
  insurance_name: '',
  invoice_number: '',
  nfse_number: '',
  bank_account: '',
  num_installments: '1',
});

const getFileIcon = (type: string) => {
  if (type.startsWith('image/')) return <ImageIcon sx={{ color: '#8b5cf6' }} />;
  if (type === 'application/pdf') return <PdfIcon sx={{ color: '#dc2626' }} />;
  return <FileIcon sx={{ color: '#6b7280' }} />;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

/* ── Extended metadata serialization in notes (PostgREST compatibility) ── */

interface ArStoredAttachment {
  id: string;
  name: string;
  url: string;
  file_url?: string;
  type: string;
  size?: number;
  uploaded_at: string;
}

interface ArExtPayload {
  nfse_number?: string;
  insurance_name?: string;
  bank_account?: string;
  installments?: Installment[];
  file_attachments?: ArStoredAttachment[];
}

const AR_EXT_START = '[[AR_EXT]]';
const AR_EXT_END = '[[/AR_EXT]]';

function extractExtBlock(full: string): { inner: string; withoutExt: string } {
  if (!full?.trim()) return { inner: '', withoutExt: full || '' };
  const s = full.indexOf(AR_EXT_START);
  if (s === -1) return { inner: '', withoutExt: full };
  const e = full.indexOf(AR_EXT_END, s + AR_EXT_START.length);
  if (e === -1) return { inner: '', withoutExt: full };
  const inner = full.slice(s + AR_EXT_START.length, e).trim();
  const before = full.slice(0, s).trimEnd();
  const after = full.slice(e + AR_EXT_END.length).replace(/^\n+/, '');
  const withoutExt = [before, after].filter(Boolean).join('\n\n').trim();
  return { inner, withoutExt };
}

function pruneArExt(ext: ArExtPayload): ArExtPayload {
  const out: ArExtPayload = {};
  if (ext.nfse_number?.trim()) out.nfse_number = ext.nfse_number.trim();
  if (ext.insurance_name?.trim()) out.insurance_name = ext.insurance_name.trim();
  if (ext.bank_account?.trim()) out.bank_account = ext.bank_account.trim();
  if (ext.installments && ext.installments.length > 1) out.installments = ext.installments;
  if (ext.file_attachments && ext.file_attachments.length > 0) {
    out.file_attachments = ext.file_attachments
      .filter((a) => a && (a.url || a.file_url) && a.name)
      .map((a) => ({
        id: String(a.id || ''),
        name: a.name,
        url: a.url || a.file_url || '',
        file_url: a.file_url || a.url,
        type: a.type || 'application/octet-stream',
        size: a.size,
        uploaded_at: a.uploaded_at || new Date().toISOString(),
      }));
  }
  return out;
}

function extFileAttachmentsToAttachments(items: ArStoredAttachment[] | undefined): Attachment[] {
  if (!items?.length) return [];
  return items.map((a) => ({
    id: a.id,
    name: a.name,
    url: a.url,
    file_url: a.file_url || a.url,
    type: a.type,
    size: a.size,
    uploaded_at: a.uploaded_at,
  }));
}

function parseClinicUploadFileUrl(data: unknown): string {
  if (!data || typeof data !== 'object') return '';
  const d = data as Record<string, unknown>;
  if (d.success === false) return '';
  const inner = d.data as Record<string, unknown> | undefined;
  if (inner && typeof inner.file_url === 'string' && inner.file_url) return inner.file_url;
  if (inner && typeof inner.url === 'string' && inner.url) return inner.url;
  if (typeof d.file_url === 'string' && d.file_url) return d.file_url;
  if (typeof d.url === 'string' && d.url) return d.url;
  return '';
}

function newAttachmentId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function parseReceivableNotes(fullNotes: string): { cleanNotes: string; ext: ArExtPayload } {
  const { inner, withoutExt } = extractExtBlock(fullNotes || '');
  let ext: ArExtPayload = {};
  if (inner) {
    try { ext = JSON.parse(inner) as ArExtPayload; } catch { /* ignore */ }
  }
  return { cleanNotes: withoutExt, ext };
}

function serializeReceivableNotes(cleanNotes: string, ext: ArExtPayload): string | undefined {
  const extPruned = pruneArExt(ext);
  const notes = cleanNotes?.trim() || '';
  if (Object.keys(extPruned).length === 0) return notes || undefined;
  const json = JSON.stringify(extPruned);
  const extBlock = `${AR_EXT_START}\n${json}\n${AR_EXT_END}`;
  return notes ? `${notes}\n\n${extBlock}` : extBlock;
}

function resolveNfseNumber(a: Pick<AccountReceivable, 'invoice_number' | 'notes'>): string {
  if (a.invoice_number) return a.invoice_number;
  return parseReceivableNotes(a.notes || '').ext.nfse_number || '';
}

/* ═══════════════════════════════════════════════════════════════
   COMPONENT
   ═══════════════════════════════════════════════════════════════ */

const AccountsReceivable: React.FC = () => {
  const { isMobile } = usePageBreakpoints();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [tabIndex, setTabIndex] = useState(0);

  // ── Accounts list ──
  const [accounts, setAccounts] = useState<AccountReceivable[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [accountsPage, setAccountsPage] = useState(0);
  const [accountsRowsPerPage, setAccountsRowsPerPage] = useState(10);
  const [accountSearch, setAccountSearch] = useState('');
  const [accountStatusFilter, setAccountStatusFilter] = useState('');
  const [accountDateFrom, setAccountDateFrom] = useState('');
  const [accountDateTo, setAccountDateTo] = useState('');

  // ── Wizard & Detail dialog ──
  const [wizardOpen, setWizardOpen] = useState(false);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<AccountReceivable | null>(null);
  const [accountForm, setAccountForm] = useState(emptyForm());
  const [detailTabIndex, setDetailTabIndex] = useState(0);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [entryMode, setEntryMode] = useState<EntryMode>('manual');

  // ── Row selection & sidebar ──
  const [selectedAccount, setSelectedAccount] = useState<AccountReceivable | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // ── Attachments ──
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Receive now dialog ──
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [receiveForm, setReceiveForm] = useState({ amount: '', discount: '0', interest: '0', penalty: '0', bank: '', notes: '', date: new Date().toISOString().slice(0, 10), payment_method: '' });

  // ── Discount dialog ──
  const [discountDialogOpen, setDiscountDialogOpen] = useState(false);
  const [discountForm, setDiscountForm] = useState({ amount: '', notes: '' });

  // ── Patient for form ──
  const [selectedPatient, setSelectedPatient] = useState<{ id: string; full_name: string; cpf?: string; phone?: string; email?: string } | null>(null);
  const [selectedProfessional, setSelectedProfessional] = useState<ProfessionalOption | null>(null);

  // ── Fixed Revenue ──
  const [fixedRevenues, setFixedRevenues] = useState<FixedRevenue[]>([]);
  const [fixedLoading, setFixedLoading] = useState(true);
  const [fixedDialogOpen, setFixedDialogOpen] = useState(false);
  const [editingFixed, setEditingFixed] = useState<FixedRevenue | null>(null);
  const [fixedForm, setFixedForm] = useState({
    description: '', category: '', amount: '', frequency: 'monthly',
    due_day: '', patient_name: '', is_active: true, notes: '',
  });

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  /* ── Build extended payload for notes serialization ── */
  const buildArExtPayload = (atts: Attachment[]): ArExtPayload => ({
    nfse_number: accountForm.nfse_number || undefined,
    insurance_name: accountForm.insurance_name || undefined,
    bank_account: accountForm.bank_account || undefined,
    installments: installments.length > 1 ? installments : undefined,
    file_attachments:
      atts.length > 0
        ? atts.map((a) => ({
            id: String(a.id || newAttachmentId()),
            name: a.name,
            url: (a.file_url || a.url || '').trim(),
            file_url: a.file_url || a.url,
            type: a.type || 'application/octet-stream',
            size: a.size,
            uploaded_at: a.uploaded_at || new Date().toISOString(),
          }))
        : undefined,
  });

  const persistReceivableNotesOnly = async (accountId: string, atts: Attachment[]) => {
    const notesPayload = serializeReceivableNotes(
      accountForm.notes,
      buildArExtPayload(atts),
    );
    await axios.put(getApiUrl(`/financial-module/accounts-receivable/${accountId}`), { notes: notesPayload });
  };

  /* ═══════════════════════════════════════
     DATA LOADING
     ═══════════════════════════════════════ */

  const loadAccounts = async () => {
    setAccountsLoading(true);
    try {
      const params: Record<string, string | number> = { page: accountsPage + 1, limit: accountsRowsPerPage };
      if (accountSearch) params.search = accountSearch;
      if (accountStatusFilter) params.status = accountStatusFilter;
      if (accountDateFrom) params.date_from = accountDateFrom;
      if (accountDateTo) params.date_to = accountDateTo;
      const response = await axios.get(getApiUrl('/financial-module/accounts-receivable'), { params });
      const resData = response.data;
      setAccounts(resData.data || resData.items || resData || []);
      setAccountsTotal(resData.total ?? resData.pagination?.total ?? (resData.data?.length || 0));
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao carregar contas a receber', severity: 'error' });
    } finally {
      setAccountsLoading(false);
    }
  };

  useEffect(() => { loadAccounts(); }, [accountsPage, accountsRowsPerPage]);

  const handleAccountSearch = () => { setAccountsPage(0); loadAccounts(); };

  /* ═══════════════════════════════════════
     INSTALLMENT GENERATION
     ═══════════════════════════════════════ */

  const generateInstallments = (total: number, count: number, firstDue: string) => {
    if (!total || count < 1 || !firstDue) return [];
    const perInstallment = Math.round((total / count) * 100) / 100;
    const pct = Math.round((100 / count) * 100) / 100;
    const items: Installment[] = [];
    for (let i = 0; i < count; i++) {
      const d = new Date(firstDue);
      d.setMonth(d.getMonth() + i);
      const isLast = i === count - 1;
      items.push({
        number: i + 1,
        due_date: d.toISOString().slice(0, 10),
        amount: isLast ? Math.round((total - perInstallment * (count - 1)) * 100) / 100 : perInstallment,
        percentage: isLast ? Math.round((100 - pct * (count - 1)) * 100) / 100 : pct,
        status: 'pending',
      });
    }
    return items;
  };

  /* ═══════════════════════════════════════
     WIZARD & DIALOG HANDLERS
     ═══════════════════════════════════════ */

  const openNewAccount = (mode: EntryMode) => {
    setWizardOpen(false);
    setEditingAccount(null);
    setEntryMode(mode);
    const form = emptyForm();
    if (mode === 'consulta') {
      form.category = 'consulta_medica';
      form.payment_method = 'particular';
    }
    if (mode === 'convenio') {
      form.category = 'convenio';
      form.payment_method = 'convenio';
    }
    setAccountForm(form);
    setSelectedPatient(null);
    setSelectedProfessional(null);
    setInstallments([]);
    setAttachments([]);
    setDetailTabIndex(0);
    setAccountDialogOpen(true);
  };

  const openEditAccount = (account: AccountReceivable) => {
    setEditingAccount(account);
    const parsed = parseReceivableNotes(account.notes || '');
    const { ext } = parsed;
    const hasInsurance = !!(account.insurance_id || ext.insurance_name);
    const mode: EntryMode = hasInsurance ? 'convenio' : account.patient_id ? 'consulta' : 'manual';
    setEntryMode(mode);
    setAccountForm({
      description: account.description,
      category: account.category,
      amount: account.amount.toString(),
      payment_method: account.payment_method,
      due_date: account.due_date,
      status: account.status,
      notes: parsed.cleanNotes,
      patient_id: account.patient_id || '',
      patient_name: account.patient_name || account.clinic_patients?.full_name || '',
      insurance_id: account.insurance_id || '',
      insurance_name: account.clinic_insurances?.name || ext.insurance_name || '',
      invoice_number: account.invoice_number || '',
      nfse_number: ext.nfse_number || account.invoice_number || '',
      bank_account: ext.bank_account || '',
      num_installments: String(account.installment_total || 1),
    });
    setSelectedPatient(
      account.patient_id
        ? { id: account.patient_id, full_name: account.patient_name || account.clinic_patients?.full_name || '' }
        : null,
    );
    setSelectedProfessional(
      account.professional_id
        ? { id: account.professional_id, full_name: account.professional_name || account.clinic_professionals?.full_name || '' }
        : null,
    );
    setInstallments(
      account.installments && account.installments.length > 0
        ? account.installments
        : ext.installments || [],
    );
    setAttachments(
      account.attachments && account.attachments.length > 0
        ? account.attachments
        : extFileAttachmentsToAttachments(ext.file_attachments),
    );
    setDetailTabIndex(0);
    setAccountDialogOpen(true);
  };

  const openEditAccountOnTab = (account: AccountReceivable, tab: number) => {
    openEditAccount(account);
    setTimeout(() => setDetailTabIndex(tab), 50);
  };

  const handleSaveAccount = async () => {
    if (!accountForm.description || !accountForm.amount || !accountForm.due_date) {
      setSnackbar({ open: true, message: 'Preencha Descrição, Valor e Vencimento', severity: 'error' });
      return;
    }
    try {
      const notesPayload = serializeReceivableNotes(accountForm.notes, buildArExtPayload(attachments));
      const payload: Record<string, unknown> = {
        description: accountForm.description,
        category: accountForm.category || undefined,
        amount: parseFloat(accountForm.amount),
        payment_method: accountForm.payment_method || undefined,
        due_date: accountForm.due_date,
        status: accountForm.status,
        notes: notesPayload,
        patient_id: selectedPatient?.id || null,
        patient_name: selectedPatient?.full_name || accountForm.patient_name || '',
        professional_id: selectedProfessional?.id || null,
        invoice_number: accountForm.nfse_number || accountForm.invoice_number || undefined,
        _user_id: user?.id,
        _user_name: user?.full_name,
      };
      if (editingAccount) {
        await axios.put(getApiUrl(`/financial-module/accounts-receivable/${editingAccount.id}`), payload);
        setSnackbar({ open: true, message: 'Conta atualizada com sucesso', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/accounts-receivable'), payload);
        setSnackbar({ open: true, message: 'Conta criada com sucesso', severity: 'success' });
      }
      setAccountDialogOpen(false);
      setEditingAccount(null);
      loadAccounts();
    } catch (error: any) {
      if (handleCashRegisterRequired(error, (p) => navigate(p))) return;
      setSnackbar({
        open: true,
        message: error.response?.data?.error || error.response?.data?.message || 'Erro ao salvar conta',
        severity: 'error',
      });
    }
  };

  const handleDeleteAccount = async (id: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta conta?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/accounts-receivable/${id}`));
      setSnackbar({ open: true, message: 'Conta excluída com sucesso', severity: 'success' });
      setSelectedAccount(null);
      setDrawerOpen(false);
      loadAccounts();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao excluir conta', severity: 'error' });
    }
  };

  const handleDuplicate = (acc: AccountReceivable) => {
    const parsed = parseReceivableNotes(acc.notes || '');
    const { ext } = parsed;
    const form = emptyForm();
    form.description = acc.description + ' (cópia)';
    form.category = acc.category;
    form.amount = acc.amount.toString();
    form.payment_method = acc.payment_method;
    form.patient_id = acc.patient_id || '';
    form.patient_name = acc.patient_name || acc.clinic_patients?.full_name || '';
    form.insurance_name = acc.clinic_insurances?.name || ext.insurance_name || '';
    form.bank_account = ext.bank_account || '';
    form.nfse_number = '';
    form.invoice_number = '';
    form.notes = parsed.cleanNotes;
    const hasInsurance = !!(acc.insurance_id || ext.insurance_name);
    const mode: EntryMode = hasInsurance ? 'convenio' : acc.patient_id ? 'consulta' : 'manual';
    setEntryMode(mode);
    setEditingAccount(null);
    setAccountForm(form);
    setSelectedPatient(
      acc.patient_id ? { id: acc.patient_id, full_name: acc.patient_name || acc.clinic_patients?.full_name || '' } : null,
    );
    setSelectedProfessional(
      acc.professional_id ? { id: acc.professional_id, full_name: acc.professional_name || acc.clinic_professionals?.full_name || '' } : null,
    );
    setInstallments([]);
    setAttachments([]);
    setDetailTabIndex(0);
    setAccountDialogOpen(true);
    setDrawerOpen(false);
  };

  /* ═══════════════════════════════════════
     RECEIVE NOW & DISCOUNT
     ═══════════════════════════════════════ */

  const handleReceiveNow = async () => {
    if (!selectedAccount || !receiveForm.amount) return;
    try {
      const receivedNow = parseFloat(receiveForm.amount);
      const totalDue = Number(selectedAccount.amount) || 0;
      const alreadyPaid = Number(selectedAccount.amount_paid || selectedAccount.paid_amount) || 0;
      const discount = parseFloat(receiveForm.discount) || 0;
      const interest = parseFloat(receiveForm.interest) || 0;
      const penalty = parseFloat(receiveForm.penalty) || 0;
      const adjustedTotal = totalDue - discount + interest + penalty;
      const newTotalPaid = alreadyPaid + receivedNow;
      const newStatus = newTotalPaid >= adjustedTotal ? 'paid' : 'partial';
      const payload = {
        status: newStatus,
        amount_paid: newTotalPaid,
        payment_date: receiveForm.date,
        payment_method: receiveForm.payment_method || undefined,
        discount,
        interest,
        penalty,
        payment_bank: receiveForm.bank || undefined,
        payment_notes: receiveForm.notes || undefined,
        _user_id: user?.id,
        _user_name: user?.full_name,
      };
      await axios.put(getApiUrl(`/financial-module/accounts-receivable/${selectedAccount.id}`), payload);
      const msg = newStatus === 'paid'
        ? 'Recebimento total registrado — status RECEBIDO'
        : `Recebimento parcial registrado — saldo pendente: R$ ${Math.max(0, adjustedTotal - newTotalPaid).toFixed(2)}`;
      setSnackbar({ open: true, message: msg, severity: 'success' });
      setReceiveDialogOpen(false);
      setDrawerOpen(false);
      setSelectedAccount(null);
      loadAccounts();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao registrar recebimento', severity: 'error' });
    }
  };

  const handleRegisterDiscount = async () => {
    if (!selectedAccount || !discountForm.amount) return;
    try {
      await axios.put(getApiUrl(`/financial-module/accounts-receivable/${selectedAccount.id}`), {
        discount: parseFloat(discountForm.amount),
        discount_notes: discountForm.notes || undefined,
        _user_id: user?.id,
        _user_name: user?.full_name,
      });
      setSnackbar({ open: true, message: 'Desconto registrado com sucesso', severity: 'success' });
      setDiscountDialogOpen(false);
      loadAccounts();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao registrar desconto', severity: 'error' });
    }
  };

  /* ═══════════════════════════════════════
     ATTACHMENTS
     ═══════════════════════════════════════ */

  const handleUploadAttachment = async (files: FileList | File[]) => {
    const accountId = editingAccount?.id;
    if (!accountId) {
      setSnackbar({ open: true, message: 'Salve a conta antes de anexar arquivos', severity: 'error' });
      return;
    }
    let nextList = [...attachments];
    for (const file of Array.from(files)) {
      setUploadingFile(true);
      setUploadProgress(0);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const response = await axios.post(getApiUrl('/financial-module/upload'), fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
            }
          },
        });
        const rd = response.data as { success?: boolean; error?: string };
        if (rd && typeof rd === 'object' && rd.success === false) {
          setSnackbar({ open: true, message: rd.error || 'Upload recusado pela API', severity: 'error' });
          continue;
        }
        const fileUrl = parseClinicUploadFileUrl(response.data);
        if (!fileUrl) {
          setSnackbar({ open: true, message: (response.data as { error?: string })?.error || 'API não retornou URL do arquivo', severity: 'error' });
          continue;
        }
        const uploaded: Attachment = {
          id: newAttachmentId(),
          name: file.name,
          url: fileUrl,
          file_url: fileUrl,
          type: file.type || 'application/octet-stream',
          size: file.size,
          uploaded_at: new Date().toISOString(),
        };
        nextList = [...nextList, uploaded];
        setAttachments(nextList);
        await persistReceivableNotesOnly(accountId, nextList);
        setSnackbar({ open: true, message: `"${file.name}" anexado com sucesso`, severity: 'success' });
      } catch (error: any) {
        setSnackbar({ open: true, message: error.response?.data?.error || `Erro ao enviar "${file.name}"`, severity: 'error' });
      } finally {
        setUploadingFile(false);
        setUploadProgress(0);
      }
    }
  };

  const handleDeleteAttachment = async (attachment: Attachment) => {
    if (!editingAccount?.id || !attachment.id) return;
    if (!window.confirm(`Excluir anexo "${attachment.name}"?`)) return;
    try {
      const next = attachments.filter((a) => a.id !== attachment.id);
      setAttachments(next);
      await persistReceivableNotesOnly(editingAccount.id, next);
      setSnackbar({ open: true, message: 'Anexo excluído', severity: 'success' });
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao excluir anexo', severity: 'error' });
    }
  };

  const handleDownloadAttachment = (attachment: Attachment) => {
    const url = attachment.file_url || attachment.url;
    if (url) window.open(url, '_blank');
  };

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }, []);
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files?.length) void handleUploadAttachment(e.dataTransfer.files);
  };

  const handleSelectRow = (acc: AccountReceivable) => {
    setSelectedAccount(acc);
    if (isMobile) setDrawerOpen(true);
  };

  /* ═══════════════════════════════════════
     FIXED REVENUE
     ═══════════════════════════════════════ */

  const loadFixedRevenues = async () => {
    setFixedLoading(true);
    try {
      const response = await axios.get(getApiUrl('/financial-module/fixed-revenue'));
      const resData = response.data;
      setFixedRevenues(resData.data || resData.items || resData || []);
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao carregar receitas fixas', severity: 'error' });
    } finally { setFixedLoading(false); }
  };

  useEffect(() => { if (tabIndex === 1) loadFixedRevenues(); }, [tabIndex]);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    setTabIndex(p.get('tab') === 'fixed' ? 1 : 0);
  }, [location.search]);

  const handleChangeTab = (_: React.SyntheticEvent, n: number) => {
    setTabIndex(n);
    navigate(n === 1 ? '/financial/accounts-receivable?tab=fixed' : '/financial/accounts-receivable');
  };

  const openFixedDialog = (item?: FixedRevenue) => {
    if (item) {
      setEditingFixed(item);
      setFixedForm({
        description: item.description, category: item.category, amount: item.amount.toString(),
        frequency: item.frequency, due_day: item.due_day.toString(), patient_name: item.patient_name || '',
        is_active: item.is_active, notes: item.notes || '',
      });
    } else {
      setEditingFixed(null);
      setFixedForm({ description: '', category: '', amount: '', frequency: 'monthly', due_day: '', patient_name: '', is_active: true, notes: '' });
    }
    setFixedDialogOpen(true);
  };

  const handleSaveFixed = async () => {
    if (!fixedForm.description || !fixedForm.amount || !fixedForm.due_day) {
      setSnackbar({ open: true, message: 'Preencha os campos obrigatórios', severity: 'error' }); return;
    }
    try {
      const payload = {
        description: fixedForm.description, category: fixedForm.category || undefined,
        amount: parseFloat(fixedForm.amount), frequency: fixedForm.frequency,
        due_day: parseInt(fixedForm.due_day, 10), patient_name: fixedForm.patient_name || undefined,
        is_active: fixedForm.is_active, notes: fixedForm.notes || undefined,
      };
      if (editingFixed) {
        await axios.put(getApiUrl(`/financial-module/fixed-revenue/${editingFixed.id}`), payload);
        setSnackbar({ open: true, message: 'Receita fixa atualizada', severity: 'success' });
      } else {
        await axios.post(getApiUrl('/financial-module/fixed-revenue'), payload);
        setSnackbar({ open: true, message: 'Receita fixa criada', severity: 'success' });
      }
      setFixedDialogOpen(false);
      setEditingFixed(null);
      loadFixedRevenues();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao salvar receita fixa', severity: 'error' });
    }
  };

  const handleDeleteFixed = async (id: string) => {
    if (!window.confirm('Excluir esta receita fixa?')) return;
    try {
      await axios.delete(getApiUrl(`/financial-module/fixed-revenue/${id}`));
      setSnackbar({ open: true, message: 'Receita fixa excluída', severity: 'success' });
      loadFixedRevenues();
    } catch (error: any) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Erro ao excluir', severity: 'error' });
    }
  };

  /* ═══════════════════════════════════════
     DERIVED STATE
     ═══════════════════════════════════════ */

  const summaryTotals = useMemo(() => {
    const total = accounts.reduce((s, a) => s + (a.amount || 0), 0);
    const received = accounts.reduce((s, a) => s + (a.amount_paid || 0), 0);
    return { total, received };
  }, [accounts]);

  const sidebarActions = [
    { icon: <EditIcon fontSize="small" />, label: 'Editar', action: () => selectedAccount && openEditAccount(selectedAccount) },
    { icon: <DuplicateIcon fontSize="small" />, label: 'Duplicar', action: () => selectedAccount && handleDuplicate(selectedAccount) },
    { icon: <ReceiveNowIcon fontSize="small" />, label: 'Receber Agora', color: ACCENT, action: () => {
      if (!selectedAccount) return;
      setReceiveForm({ amount: selectedAccount.amount.toString(), discount: '0', interest: '0', penalty: '0', bank: '', notes: '', date: new Date().toISOString().slice(0, 10), payment_method: selectedAccount.payment_method || '' });
      setReceiveDialogOpen(true);
    }},
    { icon: <DiscountIcon fontSize="small" />, label: 'Registrar Desconto', action: () => {
      setDiscountForm({ amount: '', notes: '' });
      setDiscountDialogOpen(true);
    }},
    { icon: <ReceiptIcon fontSize="small" />, label: 'Gerar Recibo', action: () => setSnackbar({ open: true, message: 'Recibo gerado (simulação)', severity: 'success' }) },
    { icon: <AttachIcon fontSize="small" />, label: 'Anexos', action: () => { if (selectedAccount) openEditAccountOnTab(selectedAccount, 3); setDrawerOpen(false); } },
    { icon: <DeleteIcon fontSize="small" />, label: 'Excluir', color: '#dc2626', action: () => selectedAccount && handleDeleteAccount(selectedAccount.id) },
  ];

  /* ═══════════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════════ */

  return (
    <Box sx={pageContainerSx}>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUpIcon sx={{ fontSize: 32, color: ACCENT }} />
          <Typography variant={isMobile ? 'h6' : 'h5'} sx={{ fontWeight: 'bold' }}>Contas a Receber</Typography>
        </Box>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Tabs value={tabIndex} onChange={handleChangeTab} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Listar" />
          <Tab label="Receitas Fixas" />
        </Tabs>

        {/* ══════════════════ TAB 0: LISTAR ══════════════════ */}
        <TabPanel value={tabIndex} index={0}>
          <Box sx={{ px: 2 }}>
            <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
              <Grid item xs={12} sm={3}>
                <TextField label="Buscar" fullWidth size="small" value={accountSearch}
                  onChange={(e) => setAccountSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAccountSearch()}
                  InputProps={{ endAdornment: <IconButton size="small" onClick={handleAccountSearch}><SearchIcon fontSize="small" /></IconButton> }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select value={accountStatusFilter} onChange={(e) => { setAccountStatusFilter(e.target.value); setAccountsPage(0); }} label="Status">
                    {STATUS_OPTIONS.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="De" type="date" fullWidth size="small" value={accountDateFrom}
                  onChange={(e) => setAccountDateFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={6} sm={2}>
                <TextField label="Até" type="date" fullWidth size="small" value={accountDateTo}
                  onChange={(e) => setAccountDateTo(e.target.value)} InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={3}>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Button variant="contained" size="small" onClick={handleAccountSearch} disabled={accountsLoading}>Filtrar</Button>
                  <Button variant="contained" size="small" startIcon={<AddIcon />} onClick={() => setWizardOpen(true)}
                    sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>Nova Conta</Button>
                  <IconButton size="small" onClick={loadAccounts} disabled={accountsLoading}><RefreshIcon fontSize="small" /></IconButton>
                </Stack>
              </Grid>
            </Grid>

            <Box sx={{ display: 'flex', gap: 0 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {accountsLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : (
                  <>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 700 }}>Situação</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Paciente / Descrição</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Profissional</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Valor</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>Recebido</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Método</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {accounts.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center">
                                <Typography color="text.secondary" sx={{ py: 3 }}>Nenhuma conta encontrada</Typography>
                              </TableCell>
                            </TableRow>
                          ) : accounts.map((acc) => {
                            const isSelected = selectedAccount?.id === acc.id;
                            const patientDisplay = acc.clinic_patients?.full_name || acc.patient_name;
                            const professionalDisplay = acc.clinic_professionals?.full_name || acc.professional_name;
                            return (
                              <TableRow key={acc.id} hover selected={isSelected}
                                onClick={() => handleSelectRow(acc)}
                                sx={{ cursor: 'pointer', '&.Mui-selected': { bgcolor: '#ecfdf5' } }}>
                                <TableCell>
                                  <Chip label={getStatusLabel(acc.status)} size="small" color={getStatusColor(acc.status)}
                                    sx={{ fontWeight: 600, fontSize: '0.7rem' }} />
                                </TableCell>
                                <TableCell>
                                  {patientDisplay && (
                                    <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{patientDisplay}</Typography>
                                  )}
                                  <Typography variant="body2" sx={{ fontWeight: patientDisplay ? 400 : 500, color: patientDisplay ? 'text.secondary' : 'text.primary', fontSize: '0.8rem' }}>
                                    {acc.description}
                                  </Typography>
                                  {acc.installment_total && acc.installment_total > 1 && (
                                    <Typography variant="caption" color="text.secondary">
                                      Parcela {acc.installment_number}/{acc.installment_total}
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.78rem', color: professionalDisplay ? '#166534' : 'text.disabled' }}>
                                    {professionalDisplay || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 'bold', color: ACCENT, fontSize: '0.8rem' }}>
                                    {formatCurrency(acc.amount)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main', fontSize: '0.8rem' }}>
                                    {formatCurrency(acc.amount_paid || 0)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDate(acc.due_date)}</Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {getPaymentMethodLabel(acc.payment_method) || '-'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                    <Tooltip title="Ver detalhes">
                                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleSelectRow(acc); setDrawerOpen(true); }}>
                                        <ViewIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Editar">
                                      <IconButton size="small" color="primary" onClick={(e) => { e.stopPropagation(); openEditAccount(acc); }}>
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 1, py: 0.5, borderTop: '1px solid #e5e7eb' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                        Total: {formatCurrency(summaryTotals.total)} &nbsp;|&nbsp; Recebido: {formatCurrency(summaryTotals.received)}
                      </Typography>
                    </Box>
                    <TablePagination component="div" count={accountsTotal} page={accountsPage}
                      onPageChange={(_, p) => setAccountsPage(p)} rowsPerPage={accountsRowsPerPage}
                      onRowsPerPageChange={(e) => { setAccountsRowsPerPage(parseInt(e.target.value, 10)); setAccountsPage(0); }}
                      rowsPerPageOptions={[5, 10, 25, 50]} labelRowsPerPage="Por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`} />
                  </>
                )}
              </Box>

              {/* Desktop sidebar */}
              {!isMobile && selectedAccount && (
                <Box sx={{ width: 240, borderLeft: '1px solid #e5e7eb', pl: 1, ml: 1, flexShrink: 0 }}>
                  <Box sx={{ p: 1.5, borderBottom: '1px solid #f1f5f9' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#1e293b' }}>
                      {selectedAccount.clinic_patients?.full_name || selectedAccount.patient_name || selectedAccount.description}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block">
                      {formatCurrency(selectedAccount.amount)} — {getStatusLabel(selectedAccount.status)}
                    </Typography>
                    {resolveNfseNumber(selectedAccount) && (
                      <Typography variant="caption" color="text.secondary">NFS-e: {resolveNfseNumber(selectedAccount)}</Typography>
                    )}
                    {selectedAccount.installment_total && selectedAccount.installment_total > 1 && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        Parcela: {selectedAccount.installment_number}/{selectedAccount.installment_total}
                      </Typography>
                    )}
                  </Box>
                  <List dense disablePadding>
                    {sidebarActions.map((a, i) => (
                      <ListItemButton key={i} onClick={a.action} sx={{ py: 0.7 }}>
                        <ListItemIcon sx={{ minWidth: 32, color: (a as any).color || 'inherit' }}>{a.icon}</ListItemIcon>
                        <ListItemText primary={a.label} primaryTypographyProps={{ fontSize: '0.82rem', fontWeight: 500, color: (a as any).color || 'inherit' }} />
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          </Box>
        </TabPanel>

        {/* ══════════════════ TAB 1: RECEITAS FIXAS ══════════════════ */}
        <TabPanel value={tabIndex} index={1}>
          <Box sx={{ px: 2 }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => openFixedDialog()}
                sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>Nova Receita Fixa</Button>
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={loadFixedRevenues} disabled={fixedLoading}>Atualizar</Button>
            </Box>
            {fixedLoading ? <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box> : (
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>Descrição</TableCell><TableCell sx={{ fontWeight: 700 }}>Categoria</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Valor</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Frequência</TableCell><TableCell align="center" sx={{ fontWeight: 700 }}>Dia Venc.</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Paciente/Origem</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700 }}>Ativo</TableCell><TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                  </TableRow></TableHead>
                  <TableBody>
                    {fixedRevenues.length === 0 ? (
                      <TableRow><TableCell colSpan={8} align="center"><Typography color="text.secondary" sx={{ py: 3 }}>Nenhuma receita fixa</Typography></TableCell></TableRow>
                    ) : fixedRevenues.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell><Typography variant="body2" sx={{ fontWeight: 500 }}>{item.description}</Typography></TableCell>
                        <TableCell>{getCategoryLabel(item.category)}</TableCell>
                        <TableCell align="right"><Typography variant="body2" sx={{ fontWeight: 'bold', color: ACCENT }}>{formatCurrency(item.amount)}</Typography></TableCell>
                        <TableCell>{getFrequencyLabel(item.frequency)}</TableCell>
                        <TableCell align="center">{item.due_day}</TableCell>
                        <TableCell>{item.patient_name || '-'}</TableCell>
                        <TableCell align="center"><Chip label={item.is_active ? 'Ativo' : 'Inativo'} size="small" color={item.is_active ? 'success' : 'default'} /></TableCell>
                        <TableCell align="center">
                          <IconButton size="small" color="primary" onClick={() => openFixedDialog(item)}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteFixed(item.id)}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>
      </Paper>

      {/* ═══════════════════════════════════════════════════════════════
         WIZARD: Nova Conta a Receber
         ═══════════════════════════════════════════════════════════════ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={wizardOpen} onClose={() => setWizardOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 700, bgcolor: ACCENT, color: '#fff' }}>
          Nova Conta a Receber
          <IconButton onClick={() => setWizardOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Escolha como deseja incluir a nova conta a receber:
          </Typography>
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ borderColor: ACCENT, borderWidth: 2 }}>
              <CardActionArea onClick={() => openNewAccount('consulta')} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ConsultaIcon sx={{ fontSize: 40, color: ACCENT }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: ACCENT }}>Atendimento / Consulta</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Receita de consultas, procedimentos, exames ou tratamentos de pacientes
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
            <Card variant="outlined" sx={{ borderColor: ACCENT, borderWidth: 2 }}>
              <CardActionArea onClick={() => openNewAccount('convenio')} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ConvenioIcon sx={{ fontSize: 40, color: ACCENT }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: ACCENT }}>Convênio / Plano de Saúde</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Faturamento via convênio ou operadora de saúde com guia e autorização
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
            <Card variant="outlined" sx={{ borderColor: ACCENT, borderWidth: 2 }}>
              <CardActionArea onClick={() => openNewAccount('manual')} sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ManualIcon sx={{ fontSize: 40, color: ACCENT }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: ACCENT }}>Inserção Manual</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Receitas diversas: aluguéis, vendas de produtos, comissões, outras receitas
                    </Typography>
                  </Box>
                </Box>
              </CardActionArea>
            </Card>
          </Stack>
        </DialogContent>
      </Dialog>

      {/* ═══════════════════════════════════════════════════════════════
         DIALOG: Conta a Receber (Detalhes com Abas)
         ═══════════════════════════════════════════════════════════════ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}>
        <Box sx={{ bgcolor: ACCENT, px: 3, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {entryMode === 'consulta' && <ConsultaIcon sx={{ color: '#fff' }} />}
            {entryMode === 'convenio' && <ConvenioIcon sx={{ color: '#fff' }} />}
            {entryMode === 'manual' && <ManualIcon sx={{ color: '#fff' }} />}
            <Box>
              <Typography sx={{ color: '#fff', fontWeight: 700, lineHeight: 1.2 }}>
                {editingAccount ? 'Editar Conta a Receber' : 'Nova Conta a Receber'}
              </Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.72rem' }}>
                {entryMode === 'consulta' && 'Atendimento / Consulta'}
                {entryMode === 'convenio' && 'Convênio / Plano de Saúde'}
                {entryMode === 'manual' && 'Inserção Manual'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setAccountDialogOpen(false)} sx={{ color: '#fff' }}><CloseIcon /></IconButton>
        </Box>
        <DialogContent sx={{ p: 0 }}>
          <Tabs value={detailTabIndex} onChange={(_, n) => setDetailTabIndex(n)}
            variant="scrollable" scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2, bgcolor: '#fafafa' }}>
            <Tab label="Detalhes" />
            <Tab label="Parcelas" />
            <Tab label="Recebimentos" />
            <Tab label="Anexos" />
          </Tabs>

          {/* ── Tab 0: Detalhes (MODE-SPECIFIC) ── */}
          <TabPanel value={detailTabIndex} index={0}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>

                {/* ══ CONSULTA MODE ══ */}
                {entryMode === 'consulta' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 1 }}><Chip label="Paciente e Profissional" size="small" icon={<PatientIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600, bgcolor: '#dcfce7' }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <PatientAutocomplete
                        value={selectedPatient}
                        onChange={(p) => {
                          setSelectedPatient(p);
                          setAccountForm({ ...accountForm, patient_id: p?.id || '', patient_name: p?.full_name || '' });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfessionalAutocomplete
                        value={selectedProfessional}
                        onChange={(p) => setSelectedProfessional(p)}
                        helperText="Vincular ao profissional para cálculo de repasses"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small"><InputLabel>Categoria do Atendimento</InputLabel>
                        <Select value={accountForm.category} onChange={(e) => setAccountForm({ ...accountForm, category: e.target.value })} label="Categoria do Atendimento">
                          {CATEGORIES.filter((c) => ['consulta_medica', 'procedimento', 'exame', 'cirurgia', 'retorno', 'avaliacao', 'tratamento', 'particular'].includes(c.value))
                            .map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Valores e Vencimento" size="small" icon={<ReceiveNowIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Descrição do Serviço *" fullWidth size="small" required value={accountForm.description}
                        onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                        placeholder="Ex: Consulta Dermatológica - Avaliação"
                        helperText="Descreva o serviço prestado ao paciente" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Valor *" fullWidth size="small" required type="number" value={accountForm.amount}
                        onChange={(e) => setAccountForm({ ...accountForm, amount: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Vencimento *" fullWidth size="small" required type="date" value={accountForm.due_date}
                        onChange={(e) => setAccountForm({ ...accountForm, due_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small"><InputLabel>Forma de Recebimento</InputLabel>
                        <Select value={accountForm.payment_method} onChange={(e) => setAccountForm({ ...accountForm, payment_method: e.target.value })} label="Forma de Recebimento">
                          {PAYMENT_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="NFS-e (Opcional)" size="small" icon={<ReceiptIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Número da NFS-e" fullWidth size="small" value={accountForm.nfse_number}
                        onChange={(e) => setAccountForm({ ...accountForm, nfse_number: e.target.value })}
                        placeholder="Ex: 001234" />
                    </Grid>
                    <Grid item xs={12} sm={8}>
                      <TextField label="Conta Corrente (Banco)" fullWidth size="small" value={accountForm.bank_account}
                        onChange={(e) => setAccountForm({ ...accountForm, bank_account: e.target.value })} />
                    </Grid>
                  </>
                )}

                {/* ══ CONVÊNIO MODE ══ */}
                {entryMode === 'convenio' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 1 }}><Chip label="Paciente e Profissional" size="small" icon={<PatientIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600, bgcolor: '#dcfce7' }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <PatientAutocomplete
                        value={selectedPatient}
                        onChange={(p) => {
                          setSelectedPatient(p);
                          setAccountForm({ ...accountForm, patient_id: p?.id || '', patient_name: p?.full_name || '' });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ProfessionalAutocomplete
                        value={selectedProfessional}
                        onChange={(p) => setSelectedProfessional(p)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Nome do Convênio / Operadora *" fullWidth size="small" value={accountForm.insurance_name}
                        onChange={(e) => setAccountForm({ ...accountForm, insurance_name: e.target.value })}
                        placeholder="Ex: Unimed, SulAmérica, Bradesco Saúde"
                        helperText="Operadora ou plano de saúde responsável pelo pagamento" />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Dados do Faturamento" size="small" icon={<ConvenioIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600, bgcolor: '#dbeafe' }} /></Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Descrição (Procedimentos Realizados) *" fullWidth size="small" required value={accountForm.description}
                        onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                        placeholder="Ex: Consulta + Exame Dermatoscopia"
                        helperText="Descreva os procedimentos cobrados do convênio" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Valor da Guia *" fullWidth size="small" required type="number" value={accountForm.amount}
                        onChange={(e) => setAccountForm({ ...accountForm, amount: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Previsão de Recebimento *" fullWidth size="small" required type="date" value={accountForm.due_date}
                        onChange={(e) => setAccountForm({ ...accountForm, due_date: e.target.value })} InputLabelProps={{ shrink: true }}
                        helperText="Data prevista do repasse" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Nº da Guia / Autorização" fullWidth size="small" value={accountForm.nfse_number}
                        onChange={(e) => setAccountForm({ ...accountForm, nfse_number: e.target.value })}
                        placeholder="Ex: GUIA-2026-0001" />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size="small"><InputLabel>Categoria</InputLabel>
                        <Select value={accountForm.category} onChange={(e) => setAccountForm({ ...accountForm, category: e.target.value })} label="Categoria">
                          {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField label="Conta Corrente (Banco)" fullWidth size="small" value={accountForm.bank_account}
                        onChange={(e) => setAccountForm({ ...accountForm, bank_account: e.target.value })} />
                    </Grid>
                  </>
                )}

                {/* ══ MANUAL MODE ══ */}
                {entryMode === 'manual' && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ mb: 1 }}><Chip label="Origem do Recebimento" size="small" icon={<ManualIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <PatientAutocomplete
                        value={selectedPatient}
                        onChange={(p) => {
                          setSelectedPatient(p);
                          setAccountForm({ ...accountForm, patient_id: p?.id || '', patient_name: p?.full_name || '' });
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ProfessionalAutocomplete
                        value={selectedProfessional}
                        onChange={(p) => setSelectedProfessional(p)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Pagador (se não for paciente)" fullWidth size="small" value={accountForm.patient_name}
                        onChange={(e) => setAccountForm({ ...accountForm, patient_name: e.target.value })}
                        placeholder="Ex: Empresa X, João Silva"
                        helperText="Preencha se o pagador não está cadastrado" />
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 1 }}><Chip label="Dados da Receita" size="small" icon={<ReceiveNowIcon sx={{ fontSize: 16 }} />} sx={{ fontWeight: 600 }} /></Divider>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField label="Descrição *" fullWidth size="small" required value={accountForm.description}
                        onChange={(e) => setAccountForm({ ...accountForm, description: e.target.value })}
                        placeholder="Ex: Aluguel de Sala Março/2026" />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Valor Total *" fullWidth size="small" required type="number" value={accountForm.amount}
                        onChange={(e) => setAccountForm({ ...accountForm, amount: e.target.value })}
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Vencimento *" fullWidth size="small" required type="date" value={accountForm.due_date}
                        onChange={(e) => setAccountForm({ ...accountForm, due_date: e.target.value })} InputLabelProps={{ shrink: true }} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small"><InputLabel>Forma de Recebimento</InputLabel>
                        <Select value={accountForm.payment_method} onChange={(e) => setAccountForm({ ...accountForm, payment_method: e.target.value })} label="Forma de Recebimento">
                          {PAYMENT_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth size="small"><InputLabel>Categoria</InputLabel>
                        <Select value={accountForm.category} onChange={(e) => setAccountForm({ ...accountForm, category: e.target.value })} label="Categoria">
                          {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="NFS-e (Opcional)" fullWidth size="small" value={accountForm.nfse_number}
                        onChange={(e) => setAccountForm({ ...accountForm, nfse_number: e.target.value })} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <TextField label="Conta Corrente (Banco)" fullWidth size="small" value={accountForm.bank_account}
                        onChange={(e) => setAccountForm({ ...accountForm, bank_account: e.target.value })} />
                    </Grid>
                  </>
                )}

                {/* ── Common fields for all modes ── */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}><Chip label="Status e Observações" size="small" sx={{ fontWeight: 600 }} /></Divider>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small"><InputLabel>Status</InputLabel>
                    <Select value={accountForm.status} onChange={(e) => setAccountForm({ ...accountForm, status: e.target.value })} label="Status">
                      <MenuItem value="pending">Pendente</MenuItem>
                      <MenuItem value="paid">Recebido</MenuItem>
                      <MenuItem value="partial">Parcial</MenuItem>
                      <MenuItem value="overdue">Atrasado</MenuItem>
                      <MenuItem value="cancelled">Cancelado</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Observações / Dados Adicionais" fullWidth multiline rows={2} size="small" value={accountForm.notes}
                    onChange={(e) => setAccountForm({ ...accountForm, notes: e.target.value })} />
                </Grid>
              </Grid>
            </Box>
          </TabPanel>

          {/* ── Tab 1: Parcelas ── */}
          <TabPanel value={detailTabIndex} index={1}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Grid item xs={4} sm={2}>
                  <TextField label="Nº de Parcelas" fullWidth size="small" type="number" value={accountForm.num_installments}
                    onChange={(e) => setAccountForm({ ...accountForm, num_installments: e.target.value })}
                    inputProps={{ min: 1, max: 120 }} />
                </Grid>
                <Grid item xs={8} sm={3}>
                  <Button variant="outlined" size="small" startIcon={<CalendarIcon />}
                    onClick={() => {
                      const total = parseFloat(accountForm.amount);
                      const count = parseInt(accountForm.num_installments) || 1;
                      const due = accountForm.due_date;
                      if (total && due) setInstallments(generateInstallments(total, count, due));
                    }}
                    sx={{ textTransform: 'none' }}>
                    Gerar Parcelas
                  </Button>
                </Grid>
              </Grid>
              {installments.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#dcfce7' }}>
                        <TableCell sx={{ fontWeight: 700 }}>Situação</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Parcela</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Vencimento</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Valor</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>Percentual</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {installments.map((inst, i) => (
                        <TableRow key={i} sx={{ bgcolor: i === 0 ? '#ecfdf5' : undefined }}>
                          <TableCell><Chip label="Previsão" size="small" sx={{ bgcolor: '#dcfce7', fontWeight: 600, fontSize: '0.7rem' }} /></TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            {String(inst.number).padStart(3, '0')}/{String(installments.length).padStart(3, '0')}
                          </TableCell>
                          <TableCell>
                            <TextField type="date" size="small" variant="standard" value={inst.due_date}
                              onChange={(e) => {
                                const copy = [...installments];
                                copy[i] = { ...copy[i], due_date: e.target.value };
                                setInstallments(copy);
                              }}
                              InputProps={{ disableUnderline: false }} sx={{ width: 140 }} />
                          </TableCell>
                          <TableCell align="right">
                            <TextField type="number" size="small" variant="standard" value={inst.amount}
                              onChange={(e) => {
                                const copy = [...installments];
                                copy[i] = { ...copy[i], amount: parseFloat(e.target.value) || 0 };
                                setInstallments(copy);
                              }}
                              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment>, disableUnderline: false }}
                              sx={{ width: 120 }} />
                          </TableCell>
                          <TableCell align="right">{inst.percentage}%</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Defina o valor total, vencimento e número de parcelas, depois clique em "Gerar Parcelas".
                </Alert>
              )}
            </Box>
          </TabPanel>

          {/* ── Tab 2: Recebimentos ── */}
          <TabPanel value={detailTabIndex} index={2}>
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Total já Recebido</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: ACCENT }}>
                      {formatCurrency(editingAccount?.amount_paid || 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Total de Descontos</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>R$ 0,00</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Juros e Multas</Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>R$ 0,00</Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2, bgcolor: '#ecfdf5' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>$ A Receber</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: ACCENT }}>
                      {formatCurrency((parseFloat(accountForm.amount) || 0) - (editingAccount?.amount_paid || 0))}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600 }}>Recebimentos Registrados</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Valor Recebido</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Desconto</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Juros</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>Multa</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Banco</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Observação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(!editingAccount?.payments || editingAccount.payments.length === 0) ? (
                      <TableRow><TableCell colSpan={7} align="center">
                        <Typography color="text.secondary" sx={{ py: 2 }}>Nenhum recebimento registrado</Typography>
                      </TableCell></TableRow>
                    ) : editingAccount.payments.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell>{formatDate(p.date)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.amount)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.discount)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.interest)}</TableCell>
                        <TableCell align="right">{formatCurrency(p.penalty)}</TableCell>
                        <TableCell>{p.bank || '-'}</TableCell>
                        <TableCell>{p.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* ── Tab 3: Anexos (Upload R2) ── */}
          <TabPanel value={detailTabIndex} index={3}>
            <Box sx={{ p: 3 }}>
              {!editingAccount ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Salve a conta primeiro para poder anexar arquivos.
                </Alert>
              ) : (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp,.doc,.docx,.xls,.xlsx,.csv,.xml,.txt,.zip"
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      if (e.target.files?.length) {
                        handleUploadAttachment(e.target.files);
                        e.target.value = '';
                      }
                    }}
                  />
                  <Box
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    sx={{
                      border: '2px dashed',
                      borderColor: isDragging ? ACCENT : '#d1d5db',
                      borderRadius: 2,
                      p: 4,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragging ? '#ecfdf5' : '#fafafa',
                      transition: 'all 0.2s ease',
                      '&:hover': { borderColor: ACCENT, bgcolor: '#ecfdf5' },
                      mb: 2,
                    }}
                  >
                    <UploadIcon sx={{ fontSize: 48, color: isDragging ? ACCENT : '#94a3b8', mb: 1 }} />
                    <Typography sx={{ fontWeight: 600, color: isDragging ? ACCENT : '#374151' }}>
                      {isDragging ? 'Solte o arquivo aqui' : 'Arraste arquivos aqui ou clique para selecionar'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                      PDF, imagens, planilhas, XML, documentos — até 25 MB por arquivo
                    </Typography>
                  </Box>

                  {uploadingFile && (
                    <Box sx={{ mb: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 6, borderRadius: 3, '& .MuiLinearProgress-bar': { bgcolor: ACCENT } }} />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Enviando... {uploadProgress}%
                      </Typography>
                    </Box>
                  )}

                  {attachments.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f9fafb' }}>
                            <TableCell sx={{ fontWeight: 700, width: 40 }}></TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Arquivo</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Tamanho</TableCell>
                            <TableCell sx={{ fontWeight: 700 }}>Data</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700 }}>Ações</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {attachments.map((att, i) => (
                            <TableRow key={att.id || i} hover>
                              <TableCell>{getFileIcon(att.type)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem' }}>{att.name}</Typography>
                                <Typography variant="caption" color="text.secondary">{att.type}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {att.size ? formatFileSize(att.size) : '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{formatDate(att.uploaded_at)}</Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                  <Tooltip title="Baixar">
                                    <IconButton size="small" onClick={() => handleDownloadAttachment(att)}
                                      disabled={!att.url && !att.file_url}>
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Excluir">
                                    <IconButton size="small" color="error" onClick={() => handleDeleteAttachment(att)}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 2 }}>
                      <Typography variant="body2" color="text.secondary">
                        Nenhum anexo ainda. Anexe comprovantes, recibos, guias, NFS-e, etc.
                      </Typography>
                    </Box>
                  )}
                </>
              )}
            </Box>
          </TabPanel>
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
          <Button onClick={() => setAccountDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveAccount}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER }, textTransform: 'none', fontWeight: 600 }}>
            {editingAccount ? 'Salvar Alterações' : 'Incluir no Contas a Receber'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DIALOG: Receber Agora ═══ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={receiveDialogOpen} onClose={() => setReceiveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReceiveNowIcon sx={{ color: ACCENT }} /> Registrar Recebimento
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Ao registrar o recebimento, o status será alterado para <strong>RECEBIDO</strong>.
          </Alert>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label="Data do Recebimento" fullWidth size="small" type="date" value={receiveForm.date}
                onChange={(e) => setReceiveForm({ ...receiveForm, date: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Valor Recebido *" fullWidth size="small" type="number" required value={receiveForm.amount}
                onChange={(e) => setReceiveForm({ ...receiveForm, amount: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de Recebimento</InputLabel>
                <Select value={receiveForm.payment_method} label="Forma de Recebimento"
                  onChange={(e) => setReceiveForm({ ...receiveForm, payment_method: e.target.value })}>
                  <MenuItem value=""><em>Não informado</em></MenuItem>
                  {PAYMENT_METHODS.map((m) => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={4}>
              <TextField label="Desconto" fullWidth size="small" type="number" value={receiveForm.discount}
                onChange={(e) => setReceiveForm({ ...receiveForm, discount: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Juros" fullWidth size="small" type="number" value={receiveForm.interest}
                onChange={(e) => setReceiveForm({ ...receiveForm, interest: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={4}>
              <TextField label="Multa" fullWidth size="small" type="number" value={receiveForm.penalty}
                onChange={(e) => setReceiveForm({ ...receiveForm, penalty: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Banco / Conta" fullWidth size="small" value={receiveForm.bank}
                onChange={(e) => setReceiveForm({ ...receiveForm, bank: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Observação" fullWidth size="small" multiline rows={2} value={receiveForm.notes}
                onChange={(e) => setReceiveForm({ ...receiveForm, notes: e.target.value })} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setReceiveDialogOpen(false)} sx={{ textTransform: 'none' }}>Cancelar</Button>
          <Button variant="contained" onClick={handleReceiveNow}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER }, textTransform: 'none', fontWeight: 600 }}>
            Registrar Recebimento
          </Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DIALOG: Registrar Desconto ═══ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={discountDialogOpen} onClose={() => setDiscountDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Registrar Desconto</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Valor do Desconto" fullWidth type="number" size="small" value={discountForm.amount}
              onChange={(e) => setDiscountForm({ ...discountForm, amount: e.target.value })}
              InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
            <TextField label="Observação" fullWidth multiline rows={2} size="small" value={discountForm.notes}
              onChange={(e) => setDiscountForm({ ...discountForm, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDiscountDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleRegisterDiscount}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>Registrar</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ DRAWER Mobile: Ações ═══ */}
      <Drawer anchor="bottom" open={isMobile && drawerOpen} onClose={() => setDrawerOpen(false)}
        PaperProps={{ sx: { borderRadius: '16px 16px 0 0', maxHeight: '60vh' } }}>
        {selectedAccount && (
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontWeight: 700, mb: 0.5 }}>
              {selectedAccount.clinic_patients?.full_name || selectedAccount.patient_name || selectedAccount.description}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formatCurrency(selectedAccount.amount)} — Venc. {formatDate(selectedAccount.due_date)}
            </Typography>
            <Divider sx={{ mb: 1 }} />
            <List dense>
              {sidebarActions.map((a, i) => (
                <ListItemButton key={i} onClick={() => { a.action(); if (a.label !== 'Receber Agora' && a.label !== 'Registrar Desconto' && a.label !== 'Editar') setDrawerOpen(false); }}>
                  <ListItemIcon sx={{ minWidth: 36, color: (a as any).color || 'inherit' }}>{a.icon}</ListItemIcon>
                  <ListItemText primary={a.label} primaryTypographyProps={{ fontWeight: 500, color: (a as any).color || 'inherit' }} />
                </ListItemButton>
              ))}
            </List>
          </Box>
        )}
      </Drawer>

      {/* ═══ Fixed Revenue Dialog ═══ */}
      <Dialog fullScreen={isMobile} scroll="paper" open={fixedDialogOpen} onClose={() => setFixedDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingFixed ? 'Editar Receita Fixa' : 'Nova Receita Fixa'}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField label="Descrição" fullWidth required value={fixedForm.description} onChange={(e) => setFixedForm({ ...fixedForm, description: e.target.value })} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth><InputLabel>Categoria</InputLabel>
                  <Select value={fixedForm.category} onChange={(e) => setFixedForm({ ...fixedForm, category: e.target.value })} label="Categoria">
                    {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField label="Valor" fullWidth required type="number" value={fixedForm.amount}
                  onChange={(e) => setFixedForm({ ...fixedForm, amount: e.target.value })}
                  InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth><InputLabel>Frequência</InputLabel>
                  <Select value={fixedForm.frequency} onChange={(e) => setFixedForm({ ...fixedForm, frequency: e.target.value })} label="Frequência">
                    {FREQUENCY_OPTIONS.map((f) => <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Dia de Vencimento" fullWidth required type="number" value={fixedForm.due_day}
                  onChange={(e) => setFixedForm({ ...fixedForm, due_day: e.target.value })} inputProps={{ min: 1, max: 31 }} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField label="Paciente / Origem" fullWidth value={fixedForm.patient_name}
                  onChange={(e) => setFixedForm({ ...fixedForm, patient_name: e.target.value })} />
              </Grid>
            </Grid>
            <FormControlLabel control={<Switch checked={fixedForm.is_active} onChange={(e) => setFixedForm({ ...fixedForm, is_active: e.target.checked })} />} label="Ativo" />
            <TextField label="Notas" fullWidth multiline rows={3} value={fixedForm.notes}
              onChange={(e) => setFixedForm({ ...fixedForm, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixedDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSaveFixed}
            sx={{ bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT_HOVER } }}>{editingFixed ? 'Atualizar' : 'Criar'}</Button>
        </DialogActions>
      </Dialog>

      {/* ═══ Snackbar ═══ */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AccountsReceivable;
